import type { BodyMeasurements } from "./types";

export interface MannequinModel {
  scale: number;
  profileName: string;
  height: number;
  headWidth: number;
  headHeight: number;
  headDepth: number;
  shoulderWidth: number;
  chestWidth: number;
  waistWidth: number;
  hipWidth: number;
  neckWidth: number;
  torsoLength: number;
  scyeDepth: number;
  armLength: number;
  measurements: {
    height: number;
    headHeight: number;
    headWidth: number;
    headDepth: number;
    chest: number;
    neck: number;
    halfBack: number;
    backNeckToWaist: number;
    scyeDepth: number;
    sleeveLength: number;
  };
}

const DISPLAY_SCALE = 0.2;

/**
 * Produce a technical proportion preview from the measurements available to
 * the pajama block. Widths based on circumferences are display estimates; this
 * is deliberately not a body-scan or a source of drafting measurements.
 */
export function buildMannequin(measurements: Record<string, number>, bodyMeasurements: BodyMeasurements, profileName: string): MannequinModel {
  const chest = measurements.chest ?? 1000;
  const neck = measurements.neck ?? 400;
  const halfBack = measurements.halfBack ?? 200;
  const backNeckToWaist = measurements.backNeckToWaist ?? 442;
  const scyeDepth = measurements.scyeDepth ?? 244;
  const sleeveLength = measurements.sleeveLength ?? 650;
  const height = bodyMeasurements.height;
  const shoulderWidth = halfBack * 2 * DISPLAY_SCALE;
  const chestWidth = Math.max(chest / Math.PI * DISPLAY_SCALE, shoulderWidth * 0.72);
  const neckWidth = neck / Math.PI * DISPLAY_SCALE;

  return {
    scale: DISPLAY_SCALE,
    profileName,
    height: height * DISPLAY_SCALE,
    headWidth: bodyMeasurements.headWidth * DISPLAY_SCALE,
    headHeight: bodyMeasurements.headHeight * DISPLAY_SCALE,
    headDepth: bodyMeasurements.headDepth * DISPLAY_SCALE,
    shoulderWidth,
    chestWidth,
    waistWidth: chestWidth * 0.78,
    hipWidth: chestWidth * 0.94,
    neckWidth,
    torsoLength: backNeckToWaist * DISPLAY_SCALE,
    scyeDepth: scyeDepth * DISPLAY_SCALE,
    armLength: sleeveLength * DISPLAY_SCALE,
    measurements: { ...bodyMeasurements, chest, neck, halfBack, backNeckToWaist, scyeDepth, sleeveLength },
  };
}
